/**
 * Cadastro aberto — sempre cria paciente (A008). Espelha o adaptador do
 * AuthContext do mobile: os dados coletados tela a tela viram o corpo de
 * POST /api/auth/register.
 */
import { api } from '../api/client';
import type { BabySex, FatherFigureFor, PatientUser } from './types';
import type { BrandRole } from '@eva/schemas';

export interface RegisterData {
  contraceptionStopped?: 'lt6' | '6to12' | 'gt12' | 'still-using' | 'prefer-not';
  dumKnowledge?: 'exact' | 'approximate' | 'unknown';
  dppKnowledge?: 'know-gestational-age' | 'remember-last-period' | 'not-informed' | 'exact' | 'approximate' | 'unknown';
  childbirthDate?: string;
  gestationalStage?: 'BEGINNING' | 'MIDDLE' | 'END';
  gestationalWeeks?: number;
  gestationalDays?: number;
  surname?: string;
  name?: string;
  email?: string;
  /** Comprovante do e-mail verificado no começo do fluxo (A029). */
  registrationToken?: string;
  /**
   * O cadastro está CONVERTENDO uma conta que já existe (A032) — alguém da
   * equipe da marca que também vira paciente. Muda o fim do fluxo: a senha
   * não é pedida, porque a conta já tem uma.
   */
  conversao?: boolean;
  cpf?: string;
  dateOfBirth?: string;
  isPostpartum?: boolean;
  isTentante?: boolean;
  nonTemporalUserType?: 'TENTANTE' | 'MAE_DE_ANJO' | 'ADOTANTE' | 'MAE';
  isFatherFigure?: boolean;
  fatherFigureFor?: FatherFigureFor;
  invitedByUserId?: string;
  babies?: Array<{ name: string | null; sex: BabySex }>;
  /**
   * Entrou pelo link (ou código) de uma empresa (A062). Atravessa o fluxo
   * inteiro: a confirmação do e-mail, o "usar esta marca também" e o
   * cadastro levam o código, e no link validado a prova do e-mail da empresa.
   */
  empresa?: { codigo: string; nome: string; comprovanteCorporativo?: string };
  /** Versão dos documentos legais aceitos em "Criar conta" (A062, fase C). */
  aceiteVersao?: string;
}

export interface RegisterPayload {
  role: 'PREGNANT_WOMAN';
  userType: string;
  fatherFigureFor?: string;
  invitedByUserId?: string;
  email: string;
  fullName: string;
  /** Opcional desde a A062: sem apelido, vale o primeiro nome. */
  preferredName?: string;
  cpf: string;
  /** Ausente quando é conversão (A032): a conta já tem senha. */
  password?: string;
  dateOfBirth?: string;
  partnerId?: string;
  partnerSlug?: string;
  /** Comprovante do e-mail verificado antes do preenchimento (A029). */
  registrationToken?: string;
  /** Link da empresa (A062). */
  codigoDaEmpresa?: string;
  comprovanteCorporativo?: string;
  /** A conta nasce antes do onboarding (A062, fase C). */
  onboardingPendente?: true;
  /** Versão dos termos e da política de privacidade aceitos. */
  aceiteTermos?: { versao: string };
  pregnancy?: {
    status: 'active' | 'completed';
    lmpDate: { type: string; date: string };
    childbirthDate?: string;
    enforcedGestationalAge: { weeks: number; days: number; referenceDate: string };
    babies: Array<{ name: string | null; sex: BabySex }>;
  };
  tryingToConceive?: { contraceptionStoppedAt: { type: 'approximate'; year: number; month: number; period: 'middle' } };
}

export const STAGE_TO_WEEKS: Record<string, number> = { BEGINNING: 8, MIDDLE: 22, END: 34 };

function faixaParaTryingToConceive(faixa?: string) {
  const mesesAtras: Record<string, number> = { lt6: 3, '6to12': 9, gt12: 18 };
  const meses = faixa ? mesesAtras[faixa] : undefined;
  if (meses === undefined) return {};
  const d = new Date();
  d.setMonth(d.getMonth() - meses);
  return { tryingToConceive: { contraceptionStoppedAt: { type: 'approximate' as const, year: d.getFullYear(), month: d.getMonth() + 1, period: 'middle' as const } } };
}

/** O que toda chamada do fluxo leva quando a pessoa entrou pelo link de uma empresa (A062). */
export function camposDaEmpresa(empresa?: RegisterData['empresa']): { codigoDaEmpresa?: string; comprovanteCorporativo?: string } {
  if (!empresa?.codigo) return {};
  return {
    codigoDaEmpresa: empresa.codigo,
    ...(empresa.comprovanteCorporativo ? { comprovanteCorporativo: empresa.comprovanteCorporativo } : {}),
  };
}

export function adaptRegisterDataToApi(data: RegisterData & { password?: string }): RegisterPayload {
  const base = {
    role: 'PREGNANT_WOMAN' as const,
    email: data.email || '',
    fullName: data.name || '',
    preferredName: data.surname || '',
    cpf: data.cpf || '',
    // Sem senha quando é conversão: a conta já tem a dela, e mandar uma nova
    // aqui trocaria a credencial de uma conta administrativa.
    ...(data.password ? { password: data.password } : {}),
    dateOfBirth: data.dateOfBirth,
    ...camposDaEmpresa(data.empresa),
  };
  const agora = new Date().toISOString();

  if (data.isFatherFigure) {
    const p: RegisterPayload = { ...base, userType: 'FATHER_FIGURE', fatherFigureFor: data.fatherFigureFor, invitedByUserId: data.invitedByUserId };
    if (!data.invitedByUserId) {
      if (data.fatherFigureFor === 'PREGNANT') {
        const weeks = data.gestationalWeeks || (data.gestationalStage ? STAGE_TO_WEEKS[data.gestationalStage] : 0);
        p.pregnancy = { status: 'active', lmpDate: { type: 'unknown', date: '' }, enforcedGestationalAge: { weeks, days: data.gestationalDays || 0, referenceDate: agora }, babies: [{ name: null, sex: 'UNKNOWN' }] };
      }
      if (data.fatherFigureFor === 'POSTPARTUM') {
        p.pregnancy = { status: 'completed', lmpDate: { type: 'unknown', date: '' }, childbirthDate: data.childbirthDate || '', enforcedGestationalAge: { weeks: 0, days: 0, referenceDate: agora }, babies: data.babies || [{ name: null, sex: 'UNKNOWN' }] };
      }
    }
    return p;
  }

  if (data.isTentante) {
    return { ...base, userType: data.nonTemporalUserType || 'TENTANTE', ...faixaParaTryingToConceive(data.contraceptionStopped) };
  }

  if (data.isPostpartum) {
    return {
      ...base, userType: 'PREGNANT',
      pregnancy: { status: 'completed', lmpDate: { type: 'unknown', date: '' }, childbirthDate: data.childbirthDate || '', enforcedGestationalAge: { weeks: 0, days: 0, referenceDate: agora }, babies: data.babies || [] },
    };
  }

  const weeks = data.gestationalWeeks || (data.gestationalStage ? STAGE_TO_WEEKS[data.gestationalStage] : 0);
  return {
    ...base, userType: 'PREGNANT',
    pregnancy: { status: 'active', lmpDate: { type: data.dumKnowledge || 'unknown', date: data.childbirthDate || '' }, enforcedGestationalAge: { weeks, days: data.gestationalDays || 0, referenceDate: agora }, babies: [{ name: null, sex: 'UNKNOWN' }] },
  };
}

export interface RegisterResult { user: PatientUser; accessToken: string; refreshToken: string }

export type SituacaoDoCadastro = 'NOVA' | 'MESMA_MARCA' | 'OUTRO_PAPEL' | 'OUTRA_MARCA';

export interface ConfirmacaoDeCadastro {
  situacao: SituacaoDoCadastro;
  /**
   * Prova que o e-mail foi verificado: em `NOVA`, para o `register` do fim;
   * em `MESMA_MARCA` (A083), para `usarSenhaNova`; em `OUTRA_MARCA`, para
   * `vincular`.
   */
  comprovante?: string;
  /** Não vem mais (A062, fase C): `MESMA_MARCA` manda entrar com a senha. */
  tokens?: { accessToken: string; refreshToken: string };
  /** `MESMA_MARCA`: quem entrou, para a casca reconstruir a sessão. */
  usuario?: PatientUser;
  /**
   * `OUTRO_PAPEL`: o que esta conta já é NESTA marca — administração,
   * conteúdo, profissional, gestora. Vem sem sessão de propósito: entrar no
   * painel pede senha.
   */
  papeisNaPorta?: BrandRole[];
  /** `OUTRO_PAPEL`: a conta pode virar também paciente (A032). */
  podeVirarPaciente?: boolean;
  /** `OUTRA_MARCA`: onde a conta vive hoje. */
  marcaDaConta?: { nome: string; slug: string | null };
  /**
   * `OUTRA_MARCA` (A052): a conta pode passar a usar esta marca também. O
   * `comprovante` autoriza `vincular`; `exigeSenha` diz se a tela pede a senha
   * atual antes (conta com papel de equipe). `podeVincular: false` é conta de
   * equipe sem cadastro de paciente — o caminho dela é outro.
   */
  podeVincular?: boolean;
  exigeSenha?: boolean;
}

/** O que a página pública do convite de paciente mostra (A052, decisão 7). */
export interface ConviteDePaciente {
  brandId: string;
  marca: { nome: string; slug: string | null };
  emailMascarado: string;
  exigeSenha: boolean;
  podeVincular: boolean;
  status: 'PENDING' | 'SENT' | 'ACCEPTED' | 'CANCELLED' | 'EXPIRED';
  /** Sem conta: a página cria a conta ali (convite por lista, A062 fase E). */
  contaExiste?: boolean;
  nomeInformado?: string | null;
}

export interface SessaoVinculada {
  tokens: { accessToken: string; refreshToken: string };
  usuario: PatientUser;
}

/** Os documentos legais vigentes (A062). */
export interface DocumentosLegais {
  versao: string;
  termosUrl: string;
  privacidadeUrl: string;
}

/** O corpo de `POST /api/me/onboarding` (A062, fase C). */
export interface DadosDoOnboarding {
  userType: 'PREGNANT' | 'TENTANTE' | 'MAE_DE_ANJO' | 'ADOTANTE' | 'MAE' | 'FATHER_FIGURE';
  fatherFigureFor?: FatherFigureFor;
  pregnancy?: RegisterPayload['pregnancy'];
  tryingToConceive?: RegisterPayload['tryingToConceive'];
  perdaGestacional?: { momento?: string | null; quando?: string | null; nomeDoBebe?: string | null };
  preferredName?: string;
  dateOfBirth?: string;
  avatarUrl?: string | null;
  telefone?: string;
  city?: string;
  uf?: string;
  /** Declarada no onboarding (A097); não vincula a empresa nenhuma. */
  empresaOndeTrabalha?: string;
}

/** O que a página pública do link da empresa recebe (A062). */
export interface LinkDaEmpresaPublico {
  /** No link da marca inteira (A062, fase D), o nome é o da marca. */
  empresa: { nome: string };
  escopo?: 'EMPRESA' | 'MARCA';
  marca: { id: string; nome: string; slug: string | null };
  modo: 'VALIDADO' | 'ABERTO';
  aceita: boolean;
  mensagem?: string;
}

export const registerApi = {
  /**
   * Autoriza uma conta da equipe da marca a virar TAMBÉM paciente (A032).
   *
   * Pede a senha atual de propósito: o código do e-mail prova a caixa de
   * entrada, e isso basta para criar conta nova — não para reescrever uma
   * conta que já tem poder na marca.
   */
  virarPaciente: (email: string, password: string, partnerSlug?: string | null) =>
    api.post<{ comprovante: string }>('/api/public/cadastro/virar-paciente', {
      email,
      password,
      ...(partnerSlug ? { partnerSlug } : {}),
    }),

  register: (payload: RegisterPayload) => api.post<RegisterResult>('/api/auth/register', payload),

  /**
   * O começo do cadastro: manda o código ANTES de qualquer preenchimento
   * (A029). Diferente de `sendEmailVerification`, que só funciona para conta
   * já criada e ainda não verificada.
   */
  enviarCodigoDeCadastro: (email: string, partnerSlug?: string | null) =>
    api.post<void>('/api/public/cadastro/codigo', { email, partnerSlug: partnerSlug || undefined }),

  /**
   * Confere o código e diz o caminho: quem não tem conta segue o cadastro com
   * o e-mail já confirmado; quem já é da marca entra direto; quem é de outra
   * marca é informado de qual.
   */
  /**
   * A conta que já existe em outra marca passa a usar esta também (A052). A
   * marca vem do comprovante emitido em `OUTRA_MARCA`, nunca do corpo.
   */
  vincular: (email: string, comprovante: string, password?: string, empresa?: RegisterData['empresa']) =>
    api.post<SessaoVinculada>('/api/public/cadastro/vincular', {
      email,
      comprovante,
      ...(password ? { password } : {}),
      ...camposDaEmpresa(empresa),
    }),

  /**
   * A paciente desta marca que se cadastrou de novo troca a senha da conta
   * pela que acabou de criar e entra (A083). O comprovante vem de
   * `MESMA_MARCA`.
   */
  usarSenhaNova: (email: string, comprovante: string, password: string) =>
    api.post<SessaoVinculada>('/api/public/cadastro/usar-senha-nova', { email, comprovante, password }),

  /** Convite de paciente existente, pelo link do e-mail (A052, decisão 7). */
  /** Convite de acesso compartilhado (A062, fase E): o que a página mostra. */
  conviteDeAcompanhamento: (token: string) =>
    api.get<{ quemConvida: string; momento: 'PREGNANT' | 'POSTPARTUM' | 'TENTANTE' | 'MAE_DE_ANJO' | 'ADOTANTE' | 'MAE'; marca: { nome: string; slug: string | null }; emailMascarado: string; contaExiste: boolean }>(
      `/api/auth/acompanhar/${encodeURIComponent(token)}`
    ),
  criarContaAcompanhando: (token: string, dados: { nome: string; senha: string; aceiteVersao: string; genero?: 'FEMININO' | 'MASCULINO' | 'OUTRO' | 'NAO_INFORMADO' }) =>
    api.post<{ user: PatientUser; accessToken: string; refreshToken: string }>(`/api/auth/acompanhar/${encodeURIComponent(token)}/criar-conta`, dados),
  convitePaciente: (token: string) => api.get<ConviteDePaciente>(`/api/public/convite-paciente/${encodeURIComponent(token)}`),
  /** Conta nova pelo convite por lista (A062, fase E): o link já provou o e-mail. */
  criarContaPeloConvite: (token: string, dados: { nome: string; senha: string; aceiteVersao: string }) =>
    api.post<{ user: PatientUser; accessToken: string; refreshToken: string }>(
      `/api/public/convite-paciente/${encodeURIComponent(token)}/criar-conta`,
      dados
    ),
  aceitarConvitePaciente: (token: string, password?: string) =>
    api.post<SessaoVinculada>(`/api/public/convite-paciente/${encodeURIComponent(token)}/aceitar`, password ? { password } : {}),

  confirmarCodigoDeCadastro: (email: string, code: string, partnerSlug?: string | null, empresa?: RegisterData['empresa']) =>
    api.post<ConfirmacaoDeCadastro>('/api/public/cadastro/confirmar', {
      email,
      code,
      partnerSlug: partnerSlug || undefined,
      ...camposDaEmpresa(empresa),
    }),

  /** Conclui o onboarding da conta recém-criada (A062, fase C). Devolve o usuário atualizado. */
  concluirOnboarding: (dados: DadosDoOnboarding) => api.post<PatientUser>('/api/me/onboarding', dados),

  /** Link da empresa para quem já é paciente desta marca e entrou com a senha (A062). */
  aplicarEmpresa: (empresa: NonNullable<RegisterData['empresa']>) =>
    api.post<{ mudou: boolean; tokens: { accessToken: string; refreshToken: string } }>('/api/me/empresa', camposDaEmpresa(empresa)),

  /** Os documentos que o cadastro manda aceitar (A062): versão e endereços. `null` = não configurados. */
  documentosLegais: () =>
    api.raw
      .get('/api/public/global-app-config')
      .then((r) => ((r.data?.data ?? r.data)?.documentosLegais ?? null) as DocumentosLegais | null)
      .catch(() => null),

  /** A página pública do link da empresa (A062). O código pode vir com hífen e minúsculo. */
  linkDaEmpresa: (codigo: string) => api.get<LinkDaEmpresaPublico>(`/api/public/empresa/${encodeURIComponent(codigo)}`),
  /** Link validado: manda o código para o e-mail da empresa. 422 = domínio de fora, com a saída na mensagem. */
  enviarCodigoCorporativo: (codigo: string, email: string) =>
    api.post<void>(`/api/public/empresa/${encodeURIComponent(codigo)}/email-corporativo/codigo`, { email }),
  confirmarCodigoCorporativo: (codigo: string, email: string, code: string) =>
    api.post<{ comprovanteCorporativo: string }>(`/api/public/empresa/${encodeURIComponent(codigo)}/email-corporativo/confirmar`, { email, code }),

  sendEmailVerification: (email: string) => api.post<void>('/api/auth/send-email-verification', { email }),
  verifyEmail: (email: string, code: string) => api.post<{ accessToken: string; refreshToken: string; user: PatientUser }>('/api/auth/verify-email', { email, code }),
  captureProspect: (email: string, source: string) => api.post<void>('/api/prospects/capture', { email, source }).catch(() => undefined),
};

/** Validação do CPF pelos dois dígitos verificadores. */
export function isValidCpf(raw: string): boolean {
  const cpf = raw.replace(/\D/g, '');
  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;
  const calc = (n: number) => {
    let soma = 0;
    for (let i = 0; i < n; i++) soma += Number(cpf[i]) * (n + 1 - i);
    const resto = (soma * 10) % 11;
    return resto === 10 ? 0 : resto;
  };
  return calc(9) === Number(cpf[9]) && calc(10) === Number(cpf[10]);
}

export const FULL_NAME_RE = /^[A-Za-zÀ-ÖØ-öø-ÿ'-]{2,}(\s+[A-Za-zÀ-ÖØ-öø-ÿ'-]{2,})+$/;
export function isValidFullName(v: string): boolean {
  return FULL_NAME_RE.test(v.trim());
}
